import { FormValues } from './copy-contents.models';

const XML_PART_SETTING_KEY = 'MetaTool_SectionMergeSettings_XmlPartId';

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function unescapeXml(text: string): string {
  return text
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&');
}

export function serializeToXml(values: FormValues): string {
  const xml = `<SectionMergeSettings xmlns="http://schemas.cmi.ch/merge-settings">
<XPath>${escapeXml(values.sources)}</XPath>
<SourceSectionBookmark>${escapeXml(values.areas)}</SourceSectionBookmark>
<InsertAfterSection>${values.targets}</InsertAfterSection>
<SetStartingNumberManual>${values.manualPageNumber}</SetStartingNumberManual>
<SupressOnDatenNachtragen>${!values.copyContentsExecute}</SupressOnDatenNachtragen>
<AddSeitenumbruch>${values.pageBreak}</AddSeitenumbruch>
</SectionMergeSettings>`;
  return xml;
}

function parseFromXml(xmlString: string): FormValues | null {
  try {
    const xpathMatch = xmlString.match(/<XPath>(.*?)<\/XPath>/s);
    const sourceBookmarkMatch = xmlString.match(
      /<SourceSectionBookmark>(.*?)<\/SourceSectionBookmark>/s
    );
    const insertAfterMatch = xmlString.match(/<InsertAfterSection>(.*?)<\/InsertAfterSection>/s);
    const setStartingNumberMatch = xmlString.match(
      /<SetStartingNumberManual>(.*?)<\/SetStartingNumberManual>/s
    );
    const supressMatch = xmlString.match(
      /<SupressOnDatenNachtragen>(.*?)<\/SupressOnDatenNachtragen>/s
    );
    const addSeitenumbruchMatch = xmlString.match(/<AddSeitenumbruch>(.*?)<\/AddSeitenumbruch>/s);

    if (!xpathMatch || !sourceBookmarkMatch || !insertAfterMatch) {
      return null;
    }

    return {
      sources: unescapeXml(xpathMatch[1].trim()),
      areas: unescapeXml(sourceBookmarkMatch[1].trim()),
      targets: parseInt(insertAfterMatch[1].trim(), 10) || 1,
      manualPageNumber: setStartingNumberMatch
        ? setStartingNumberMatch[1].trim().toLowerCase() === 'true'
        : false,
      copyContentsExecute: supressMatch ? supressMatch[1].trim().toLowerCase() !== 'true' : false,
      pageBreak: addSeitenumbruchMatch
        ? addSeitenumbruchMatch[1].trim().toLowerCase() === 'true'
        : false
    };
  } catch (error) {
    console.error('Error parsing XML:', error);
    return null;
  }
}

function isSectionMergeSettingsXml(xmlContent: string): boolean {
  return xmlContent.includes('<SectionMergeSettings');
}

export function getDefaultFormValues(): FormValues {
  return {
    sources: '',
    areas: '',
    targets: 1,
    pageBreak: false,
    copyContentsExecute: false,
    manualPageNumber: false
  };
}

async function getXmlPartIdFromSettings(context: Word.RequestContext): Promise<string | null> {
  const settings = context.document.settings;
  const xmlPartIdSetting = settings.getItemOrNullObject(XML_PART_SETTING_KEY);
  xmlPartIdSetting.load('value');
  await context.sync();

  if (!xmlPartIdSetting.isNullObject && xmlPartIdSetting.value) {
    const id = String(xmlPartIdSetting.value);
    return id;
  }

  return null;
}

async function loadCustomXmlPartById(
  context: Word.RequestContext,
  id: string
): Promise<string | null> {
  try {
    const customXmlPart = context.document.customXmlParts.getItemOrNullObject(id);
    customXmlPart.load('id');
    await context.sync();

    if (customXmlPart.isNullObject) {
      console.warn('[loadCustomXmlPartById] Part not found (isNullObject) for ID:', id);
      return null;
    }

    const xmlBlob = customXmlPart.getXml();
    await context.sync();
    return xmlBlob.value;
  } catch (error) {
    console.warn('[loadCustomXmlPartById] Error loading CustomXmlPart by ID:', id, error);
    return null;
  }
}

async function findCustomXmlPartByContent(
  context: Word.RequestContext
): Promise<{ part: Word.CustomXmlPart; xml: string } | null> {
  const customXmlParts = context.document.customXmlParts;
  customXmlParts.load('items');
  await context.sync();

  for (const part of customXmlParts.items) {
    const xmlBlob = part.getXml();
    await context.sync();
    if (isSectionMergeSettingsXml(xmlBlob.value)) {
      return { part, xml: xmlBlob.value };
    }
  }

  return null;
}

async function deleteAllSectionMergeSettingsParts(context: Word.RequestContext): Promise<void> {
  const customXmlParts = context.document.customXmlParts;
  customXmlParts.load('items');
  await context.sync();

  const partsToDelete: Word.CustomXmlPart[] = [];
  for (const part of customXmlParts.items) {
    const xmlBlob = part.getXml();
    await context.sync();

    // Check if this is a SectionMergeSettings part
    if (isSectionMergeSettingsXml(xmlBlob.value)) {
      partsToDelete.push(part);
    }
  }

  // Delete all matching parts
  for (const part of partsToDelete) {
    part.delete();
  }
  if (partsToDelete.length > 0) {
    await context.sync();
  }
}

export async function saveCustomXmlPart(
  context: Word.RequestContext,
  xmlString: string
): Promise<void> {
  const settings = context.document.settings;

  await deleteAllSectionMergeSettingsParts(context);

  const customXmlPart = context.document.customXmlParts.add(xmlString);
  customXmlPart.load('id');
  await context.sync();
  const finalId = String(customXmlPart.id);

  const xmlPartIdSetting = settings.getItemOrNullObject(XML_PART_SETTING_KEY);
  xmlPartIdSetting.load('value');
  await context.sync();

  if (!xmlPartIdSetting.isNullObject) {
    xmlPartIdSetting.delete();
    await context.sync();
  }

  settings.add(XML_PART_SETTING_KEY, finalId);
  await context.sync();

  const verifySetting = settings.getItemOrNullObject(XML_PART_SETTING_KEY);
  verifySetting.load('value');
  await context.sync();
}

export async function deleteCustomXmlPart(context: Word.RequestContext): Promise<void> {
  const settings = context.document.settings;
  const xmlPartIdSetting = settings.getItemOrNullObject(XML_PART_SETTING_KEY);
  xmlPartIdSetting.load('value');
  await context.sync();

  if (!xmlPartIdSetting.isNullObject && xmlPartIdSetting.value) {
    try {
      const customXmlPart = context.document.customXmlParts.getItem(String(xmlPartIdSetting.value));
      customXmlPart.delete();
      await context.sync();
    } catch (partError) {
      // Part might not exist, continue to remove setting
      console.warn('CustomXmlPart not found for deletion:', partError);
    }

    xmlPartIdSetting.delete();
    await context.sync();
  } else {
    const found = await findCustomXmlPartByContent(context);
    if (found) {
      found.part.delete();
      await context.sync();
    }
  }
}

export async function loadFormValuesFromDocument(
  context: Word.RequestContext
): Promise<FormValues | null> {
  const id = await getXmlPartIdFromSettings(context);

  if (id) {
    const xmlContent = await loadCustomXmlPartById(context, id);
    if (xmlContent) {
      const parsedValues = parseFromXml(xmlContent);
      return parsedValues;
    }
  }

  return null;
}

/**
 * Jumps to the specified section number in the Word document
 * @param sectionNumber - The 1-based index of the section to jump to
 * @returns Promise that resolves when the section is selected
 */
export async function jumpToSection(sectionNumber: number): Promise<void> {
  return Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load('items');
    await context.sync();

    if (sectionNumber < 1 || sectionNumber > sections.items.length) {
      throw new Error(
        `Invalid section number: ${sectionNumber}. Document has ${sections.items.length} sections.`
      );
    }

    // Get the section (1-based index, so subtract 1)
    const section = sections.items[sectionNumber - 1];
    const sectionBody = section.body;
    const sectionRange = sectionBody.getRange();

    // Select the section
    sectionRange.select();
    await context.sync();
  });
}
